/**
 * AUTO-DEBATE REPLAY PAGE
 * Displays an autonomous debate generated by the user's Personal God.
 * Fetches from /api/war-council/auto-debate/:id instead of shared_living_rooms.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, Clock, MessageCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  athena:   { name: 'Athena',   text: 'text-amber-400',   bg: 'bg-amber-500/10', border: 'border-amber-400/20', gradient: 'from-amber-400 to-orange-500' },
  hermes:   { name: 'Hermes',   text: 'text-orange-400',  bg: 'bg-orange-500/10', border: 'border-orange-400/20', gradient: 'from-orange-400 to-red-500' },
  apollo:   { name: 'Apollo',   text: 'text-yellow-400',  bg: 'bg-yellow-500/10', border: 'border-yellow-400/20', gradient: 'from-yellow-400 to-amber-500' },
  eris:     { name: 'Eris',     text: 'text-red-400',     bg: 'bg-red-500/10',    border: 'border-red-400/20',    gradient: 'from-red-400 to-rose-600' },
  calliope: { name: 'Calliope', text: 'text-pink-400',    bg: 'bg-pink-500/10',   border: 'border-pink-400/20',   gradient: 'from-pink-400 to-purple-500' },
  plutus:   { name: 'Plutus',   text: 'text-green-400',   bg: 'bg-green-500/10',  border: 'border-green-400/20',  gradient: 'from-green-400 to-emerald-600' },
  hestia:   { name: 'Hestia',   text: 'text-orange-300',  bg: 'bg-orange-400/10', border: 'border-orange-300/20', gradient: 'from-orange-300 to-amber-500' },
  themis:   { name: 'Themis',   text: 'text-blue-400',    bg: 'bg-blue-500/10',   border: 'border-blue-400/20',   gradient: 'from-blue-400 to-cyan-500' },
  nike:     { name: 'Nike',     text: 'text-yellow-300',  bg: 'bg-yellow-400/10', border: 'border-yellow-300/20', gradient: 'from-yellow-300 to-orange-400' },
  metis:    { name: 'Metis',    text: 'text-indigo-400',  bg: 'bg-indigo-500/10', border: 'border-indigo-400/20', gradient: 'from-indigo-400 to-violet-600' },
  dionysus: { name: 'Dionysus', text: 'text-purple-400',  bg: 'bg-purple-500/10', border: 'border-purple-400/20', gradient: 'from-purple-400 to-pink-500' },
  ares:     { name: 'Ares',     text: 'text-red-500',     bg: 'bg-red-500/10',    border: 'border-red-500/20',    gradient: 'from-red-500 to-rose-700' },
  personal_god: { name: 'The Thirteenth', text: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-400/30', gradient: 'from-amber-400 to-yellow-500' },
};

export default function AutoDebateReplayPage() {
  const { autoDebateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDebate = async () => {
      try {
        const res = await fetch(`${API_URL}/api/war-council/auto-debate/${autoDebateId}`);
        if (!res.ok) throw new Error('Debate not found');
        const d = await res.json();
        setData(d);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (autoDebateId) fetchDebate();
  }, [autoDebateId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-400">
        <p>{error || 'Debate not found'}</p>
      </div>
    );
  }

  const msgs = data.messages || [];
  const verdict = data.verdict;

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="auto-debate-replay">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-950/90 backdrop-blur-sm border-b border-amber-500/10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-400 font-medium">Autonomous Debate</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Initiated Banner */}
        <div className="mb-8 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-center"
             style={{ boxShadow: '0 0 30px rgba(251,191,36,0.1)' }}>
          <Sparkles className="w-6 h-6 text-amber-400 mx-auto mb-2" />
          <p className="text-amber-300 font-bold text-lg mb-1">
            Initiated by {data.initiated_by || 'The Thirteenth'}
          </p>
          <p className="text-amber-400/60 text-xs">
            Your Personal God convened this council at {data.consciousness || 100}% consciousness
          </p>
        </div>

        {/* Topic */}
        <div className="mb-6 text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-2" data-testid="auto-debate-topic">
            "{data.topic}"
          </h1>
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(data.created_at).toLocaleDateString()}</span>
            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {msgs.length} messages</span>
          </div>
        </div>

        {/* Council Participants */}
        <div className="flex justify-center gap-2 mb-8">
          {(data.gods || []).map(g => {
            const cfg = GOD_CONFIG[g] || GOD_CONFIG.athena;
            return (
              <div key={g} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-800/50 border border-gray-700/50">
                <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${cfg.gradient}`} />
                <span className={`text-xs font-medium ${cfg.text}`}>{cfg.name}</span>
              </div>
            );
          })}
        </div>

        {/* Messages */}
        <div className="space-y-4 mb-8">
          {msgs.map((msg, i) => {
            const isPersonalGod = msg.is_personal_god || msg.agent === 'personal_god';
            const godId = isPersonalGod ? 'personal_god' : msg.agent;
            const cfg = GOD_CONFIG[godId] || GOD_CONFIG.athena;
            const name = isPersonalGod ? (data.initiated_by || 'The Thirteenth') : (msg.name || cfg.name);

            return (
              <div key={i} className={`flex items-start gap-3 ${isPersonalGod ? 'pl-2' : ''}`}
                   style={isPersonalGod ? { borderLeft: '2px solid rgba(251,191,36,0.3)', paddingLeft: '12px' } : {}}
                   data-testid={`auto-msg-${i}`}>
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${cfg.gradient} flex items-center justify-center flex-shrink-0 text-white text-xs font-bold
                  ${isPersonalGod ? 'ring-2 ring-amber-400/40' : ''}`}
                     style={isPersonalGod ? { boxShadow: '0 0 12px rgba(251,191,36,0.4)' } : {}}>
                  {isPersonalGod ? <Sparkles className="w-3.5 h-3.5" /> : name[0]}
                </div>
                <div className="max-w-[85%]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${cfg.text}`}>{name}</span>
                    {isPersonalGod && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-400 font-medium">
                        YOUR GOD
                      </span>
                    )}
                  </div>
                  <div className={`px-4 py-3 rounded-2xl rounded-tl-sm ${cfg.bg} border ${cfg.border}`}>
                    <p className="text-gray-200 text-sm leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Verdict */}
        {verdict && (
          <div className="p-5 rounded-xl bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/20"
               data-testid="auto-debate-verdict">
            <h3 className="text-sm font-bold text-purple-300 mb-2">Council Verdict</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {typeof verdict === 'string' ? verdict : verdict?.summary || verdict?.content || JSON.stringify(verdict)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
